import {
  THREAD_KINDS,
  type ResponderAnswer,
  type ThreadKind,
  type TranscriptFormattingPort,
} from '@/shared-responder-v2-core';

import { isDraftOverAnswerLimit } from './shared-responder-v2-forward-block.helpers';
import { settleWithin } from './shared-responder-v2-spoken-pick.helpers';

export const TRANSCRIPT_FORMAT_TIMEOUT_MS = 4000;

// The backend only rewrites an `OpenEnded` transcript; every other type comes back trimmed.
export interface TranscriptFormatQueryInput {
  readonly questionType: 'OpenEnded';
  readonly transcript: string;
}

export function toTranscriptFormatQueryInput(
  transcript: string
): TranscriptFormatQueryInput {
  return { questionType: 'OpenEnded', transcript: transcript.trim() };
}

type TextAnswer = Extract<ResponderAnswer, { kind: 'text' }>;

// ContactInfo drafts as `text` too, but holds a name, email or phone the formatter must not rewrite.
function isProseAnswer(
  kind: ThreadKind | null,
  answer: ResponderAnswer
): answer is TextAnswer {
  return (
    answer.kind === 'text' && kind !== null && kind !== THREAD_KINDS.ContactInfo
  );
}

export async function formatProseTranscript(
  answer: ResponderAnswer,
  kind: ThreadKind | null,
  port: TranscriptFormattingPort
): Promise<ResponderAnswer> {
  if (!port.isAvailable || !isProseAnswer(kind, answer)) {
    return answer;
  }

  const transcript = answer.transcript?.trim() ?? '';

  if (transcript === '') {
    return answer;
  }

  const formatted = await settleWithin(
    port.format(transcript),
    TRANSCRIPT_FORMAT_TIMEOUT_MS
  );

  if (formatted === null) {
    return answer;
  }

  const isUntypedText =
    answer.text.trim() === '' || answer.text.trim() === transcript;

  const formattedAnswer: TextAnswer = {
    kind: 'text',
    text: isUntypedText ? formatted : answer.text,
    transcript: formatted,
  };

  return isDraftOverAnswerLimit(formattedAnswer) ? answer : formattedAnswer;
}
